import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { ConfirmDialog } from "../../../ui/Dialog.js";

/**
 * Warns before unsaved work is lost (spec §14 "Navigating away with unsaved
 * changes produces a warning. Unsaved data is discarded when the page is
 * closed or the warning is confirmed.").
 *
 * Two doors out of a page are watched:
 *
 * - an in-app navigation (a sidebar link, Back) is held by the router until
 *   the person chooses Discard changes or Keep editing;
 * - closing or reloading the tab gets the browser's own "leave site?" prompt
 *   through `beforeunload` — the browser never lets a page draw its own.
 *
 * `when` is read *at the moment* a navigation or unload is attempted, so a
 * caller can pass a function over refs and turn the guard off in the same
 * tick it navigates after a save (a boolean prop would still be the value
 * of the last render). An explicit Cancel is the caller's business: it turns
 * the guard off and navigates, because choosing Cancel is the decision.
 */

export interface UnsavedChangesGuardProps {
  /** Whether leaving now would lose something; a function is evaluated when it matters. */
  when: boolean | (() => boolean);
  title?: string;
  description?: string;
  discardLabel?: string;
  keepLabel?: string;
}

export function UnsavedChangesGuard({
  when,
  title = "Discard unsaved changes?",
  description = "This order hasn't been created. If you leave now, what you typed is lost.",
  discardLabel = "Discard changes",
  keepLabel = "Keep editing",
}: UnsavedChangesGuardProps) {
  const whenRef = useRef(when);
  whenRef.current = when;
  const isDirty = useCallback(() => {
    const current = whenRef.current;
    return typeof current === "function" ? current() : current;
  }, []);

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        isDirty() &&
        (currentLocation.pathname !== nextLocation.pathname ||
          currentLocation.search !== nextLocation.search),
      [isDirty],
    ),
  );

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty()) return;
      event.preventDefault();
      // Legacy browsers read this; modern ones show their own generic text.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  // If the work was saved (or discarded) while the question was up, the
  // question no longer applies: let the navigation through.
  useEffect(() => {
    if (blocker.state === "blocked" && !isDirty()) blocker.proceed();
  }, [blocker, isDirty]);

  return (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      onClose={() => {
        if (blocker.state === "blocked") blocker.reset();
      }}
      onConfirm={() => {
        if (blocker.state === "blocked") blocker.proceed();
      }}
      title={title}
      description={description}
      confirmLabel={discardLabel}
      cancelLabel={keepLabel}
      tone="destructive"
    />
  );
}
